/**
 * VideoDebugOverlay — sichtbares Debug-HUD für Video-Playback-Diagnose.
 *
 * Aktiv nur bei `?videodebug=1` im URL. Pollt 5×/s alle `<video>`-Elements
 * im Document und zeigt pro Element den Live-State (paused, currentTime,
 * readyState, networkState, error). Gedacht für iPhone-Tests wo wir keine
 * DevTools-Console haben.
 *
 * Erstellt 2026-05-27 als Diagnose für Becker4.mov-Playback-Issue auf iOS
 * Safari (User-Befund: "minimale Bewegung sichtbar, sonst nichts").
 */

import { useEffect, useState } from "react";

interface VideoState {
  src: string;
  paused: boolean;
  muted: boolean;
  currentTime: number;
  duration: number;
  readyState: number;
  networkState: number;
  error: string | null;
  width: number;
  height: number;
  inDom: boolean;
}

const READY_STATE_LABEL = [
  "HAVE_NOTHING",
  "HAVE_METADATA",
  "HAVE_CURRENT_DATA",
  "HAVE_FUTURE_DATA",
  "HAVE_ENOUGH_DATA",
];

const NETWORK_STATE_LABEL = ["EMPTY", "IDLE", "LOADING", "NO_SOURCE"];

const MEDIA_ERROR_LABEL = [
  "0",
  "ABORTED",
  "NETWORK",
  "DECODE",
  "SRC_NOT_SUPPORTED",
];

function snapshot(): VideoState[] {
  if (typeof document === "undefined") return [];
  const vids = Array.from(document.getElementsByTagName("video"));
  return vids.map((v) => ({
    src: v.src.split("/").slice(-1)[0]?.slice(0, 30) ?? "",
    paused: v.paused,
    muted: v.muted,
    currentTime: v.currentTime,
    duration: v.duration,
    readyState: v.readyState,
    networkState: v.networkState,
    error: v.error
      ? `${MEDIA_ERROR_LABEL[v.error.code] ?? v.error.code}: ${v.error.message}`
      : null,
    width: v.videoWidth,
    height: v.videoHeight,
    inDom: document.body.contains(v),
  }));
}

export function VideoDebugOverlay() {
  const [enabled, setEnabled] = useState(false);
  const [states, setStates] = useState<VideoState[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("videodebug") !== "1") return;
    setEnabled(true);
    const tick = () => setStates(snapshot());
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, []);

  if (!enabled) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 99999,
        background: "rgba(0,0,0,0.92)",
        color: "#0f0",
        fontFamily: "ui-monospace, Menlo, monospace",
        fontSize: 10,
        lineHeight: 1.3,
        padding: 8,
        maxHeight: "40vh",
        overflowY: "auto",
        pointerEvents: "none",
        borderTop: "1px solid #0f0",
      }}
    >
      <div style={{ marginBottom: 4, color: "#ff0" }}>
        videos in DOM: {states.length}
      </div>
      {states.length === 0 && (
        <div style={{ color: "#f00" }}>
          NO &lt;video&gt; ELEMENTS IN DOM — pre-warming oder DOM-attach failed
        </div>
      )}
      {states.map((s, i) => {
        const isStuck = !s.paused && s.currentTime === 0 && s.readyState >= 2;
        const isPlaying = !s.paused && s.currentTime > 0;
        const color = s.error
          ? "#f33"
          : isPlaying
            ? "#0f0"
            : isStuck
              ? "#fa0"
              : s.paused
                ? "#999"
                : "#fff";
        return (
          <div key={i} style={{ color, marginBottom: 2 }}>
            [{i}] {s.src} {s.width}×{s.height}
            {" | "}
            {s.paused ? "PAUSED" : "PLAY"} t={s.currentTime.toFixed(2)}/
            {isFinite(s.duration) ? s.duration.toFixed(2) : "?"}
            {" | "}
            rs={READY_STATE_LABEL[s.readyState] ?? s.readyState}
            {" | "}
            ns={NETWORK_STATE_LABEL[s.networkState] ?? s.networkState}
            {" | "}
            muted={s.muted ? "Y" : "N"}
            {!s.inDom && " | OFF-DOM"}
            {s.error && ` | ERR: ${s.error}`}
          </div>
        );
      })}
    </div>
  );
}
